<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'meterConsumptions',
    path: '/meter-consumptions',
    component: 'MeterConsumptionBrowser',
    title: () => gettext('Meter Consumptions'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterConsumptionBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const onCreateInvoicesPayload = (...args) => {
    return { consumptions: _.map(selectedItems.value, 'id') };
  };
  const onGenerateMeterConsumptions = (...args) => {
    return $imodal.value
      .show({ name: 'GenerateMeterConsumptions' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return result.value;
      });
  };

  const gridName = ref('meterConsumptions');
  const selectedItems = ref([]);
  const dsParams = ref({
    unbilled_only: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
    selectable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'invoice',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Invoice'),
      params: {
        sortPath: 'invoice.invoice_number',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <a href="#" onclick="return false;">\n\
      <strong title="{{ $value.invoice_number }}">{{ $value.invoice_number }}</strong>\n\
    </a>\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'meter',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Meter'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.serial_number }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'meter',
            navigateOnIconClick: true,
            params: {
              id: 'meter.id',
            },
          },
        ],
      },
    },
    {
      field: 'start.date',
      type: 'date',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Start date'),
      params: {
        sortPath: 'start.date',
      },
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'start.date',
          },
        },
      ],
    },
    {
      field: 'start.reading',
      type: 'number',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Start reading'),
      params: {
        sortPath: 'start.reading',
      },
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'start.reading',
          },
        },
        {
          name: 'number',
        },
      ],
    },
    {
      field: 'end.date',
      type: 'date',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'End date'),
      params: {
        sortPath: 'end.date',
      },
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'end.date',
          },
        },
      ],
    },
    {
      field: 'end.reading',
      type: 'number',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'End reading'),
      params: {
        sortPath: 'end.reading',
      },
      formatter: [
        {
          name: 'getvalue',
          opts: {
            field: 'end.reading',
          },
        },
        {
          name: 'number',
        },
      ],
    },
    {
      field: 'total_consumption',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Total'),
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Unit'),
    },
    {
      field: 'non_billable',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Non-billable'),
      formatter: 'checkbox',
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental.id',
            },
          },
        ],
      },
    },
    {
      field: 'consumer',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Consumer'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.code }} | {{ $value.name }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'consumer.id',
            },
          },
        ],
      },
    },
    {
      field: 'invoices',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Invoices'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'invoice_number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'invoice',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/browse_meter_consumption', {
      parameters: () => ({
        ...dsParams.value,
        unbilled_only: dsParams.value.unbilled_only ? 1 : null,
      }),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'MeterConsumptionBrowser.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Meter Consumptions'),
      icon: $config.value.meterConsumption.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_q8xeh2ulx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_p642jx9bf_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2095089031469157_showModal_z0020ese6_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'UploadMeterReadings',
    };
  };
  const act_397195341670858_reloadSlickgrid_hf0prdd64_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_397195341670858_close_6sx8jdx1d_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_397195341670858_showModal_erjr9gyqr_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterConsumption',
    };
  };
  const act_8823104369878821_reloadSlickgrid_6hxyxohtf_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'meterConsumptions',
    };
  };
  const act_8823104369878821_request_fs9bdx5qi_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_delete_meter_consumption',
      data: {
        items: _.map(selectedItems.value, (p) => ({ id: p.id })),
      },
    };
  };

  const act_7315092382398562_closeModal_q8xeh2ulx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_q8xeh2ulx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_jebnj3fuw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_q8xeh2ulx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_jebnj3fuw],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_qo2knlk6z =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_g3hcw9rry =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qo2knlk6z,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_g3hcw9rry],
    event: 'reload',
  });
  const act_2248226175642056_setData_p642jx9bf = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_p642jx9bf_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_95007y3hs =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_p642jx9bf,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_95007y3hs],
      event: 'selectedRowsChanged',
    });
  const act_2095089031469157_showModal_z0020ese6 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_z0020ese6_getActionArgs,
    });
  const inst_evh_2095089031469157_click_dta2sty6q =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_z0020ese6,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_dta2sty6q],
    event: 'click',
  });
  const act_397195341670858_reloadSlickgrid_hf0prdd64 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_397195341670858_reloadSlickgrid_hf0prdd64_getActionArgs,
    });
  const inst_evh_397195341670858_close_6sx8jdx1d =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_reloadSlickgrid_hf0prdd64,
      event: 'close',
      when: inst_evh_397195341670858_close_6sx8jdx1d_getWhen,
      displayName: 'reloadSlickgrid',
    });
  const act_397195341670858_showModal_erjr9gyqr =
    new actionTypes.ShowModalAction({
      actionArgs: act_397195341670858_showModal_erjr9gyqr_getActionArgs,
      events: [inst_evh_397195341670858_close_6sx8jdx1d],
    });
  const inst_evh_397195341670858_click_z6wbqi2oq =
    new actionTypes.EventHandlerImpl({
      action: act_397195341670858_showModal_erjr9gyqr,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_397195341670858_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_397195341670858_click_z6wbqi2oq],
    event: 'click',
  });
  const act_8823104369878821_reloadSlickgrid_6hxyxohtf =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_8823104369878821_reloadSlickgrid_6hxyxohtf_getActionArgs,
    });
  const inst_evh_8823104369878821_success_i0gwc3nkh =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_reloadSlickgrid_6hxyxohtf,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_8823104369878821_request_fs9bdx5qi = new actionTypes.RequestAction({
    actionArgs: act_8823104369878821_request_fs9bdx5qi_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_8823104369878821_success_i0gwc3nkh],
  });
  const inst_evh_8823104369878821_click_ormzxhr8v =
    new actionTypes.EventHandlerImpl({
      action: act_8823104369878821_request_fs9bdx5qi,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_8823104369878821_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8823104369878821_click_ormzxhr8v],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_jebnj3fuw,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_g3hcw9rry,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_95007y3hs,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2095089031469157_click_dta2sty6q,
    inst_evh_2095089031469157_click,
    inst_evh_397195341670858_close_6sx8jdx1d,
    inst_evh_397195341670858_click_z6wbqi2oq,
    inst_evh_397195341670858_click,
    inst_evh_8823104369878821_success_i0gwc3nkh,
    inst_evh_8823104369878821_click_ormzxhr8v,
    deleteItems: inst_evh_8823104369878821_click_ormzxhr8v,
    inst_evh_8823104369878821_click,
  };

  const evh_7315092382398562_close_jebnj3fuw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_jebnj3fuw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_g3hcw9rry = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_g3hcw9rry.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_95007y3hs = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_95007y3hs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_dta2sty6q = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_dta2sty6q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_close_6sx8jdx1d = async (event, scope) => {
    return await actions.inst_evh_397195341670858_close_6sx8jdx1d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click_z6wbqi2oq = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click_z6wbqi2oq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_397195341670858_click = async (event, scope) => {
    return await actions.inst_evh_397195341670858_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_success_i0gwc3nkh = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_success_i0gwc3nkh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click_ormzxhr8v = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click_ormzxhr8v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8823104369878821_click = async (event, scope) => {
    return await actions.inst_evh_8823104369878821_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterConsumptionBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="meterConsumptions"
      :headerTitle="pgettext('MeterConsumptionBrowser', 'Meter Consumptions')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="
        pgettext('MeterConsumptionBrowser|ExportFilename', 'meter-consumptions')
      "
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="unbilled_only"
              :label="pgettext('MeterConsumptionBrowser', 'Unbilled only')"
              inline
              v-model="dsParams.unbilled_only"
              :value="dsParams.unbilled_only"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (dsParams.unbilled_only =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpButton
            :icon="$config.uploadIcon"
            :text="pgettext('MeterConsumptionBrowser', 'Upload readings...')"
            size="sm"
            variant="light"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <JobHandlerControl
            jobType="generate_meter_consumptions"
            load-current
            :payload="onGenerateMeterConsumptions"
            icon="material-symbols:cloud-sync-outline-rounded"
            :text="
              pgettext(
                'GenerateMeterConsumptions|Submit',
                'Generate Consumptions'
              )
            "
            size="sm"
          >
          </JobHandlerControl>
          <ItpButton
            :icon="$config.meterConsumption.createMeterConsumptionIcon"
            :text="pgettext('MeterConsumptionBrowser', 'Add consumption...')"
            size="sm"
            variant="light"
            @click="evh_397195341670858_click($event, {})"
          >
          </ItpButton>
          <ItpButton
            icon="fa-trash"
            :title="pgettext('MeterConsumptionBrowser', 'Delete consumption')"
            :text="gettext('Delete')"
            size="sm"
            variant="light"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_8823104369878821_click($event, {})"
          >
          </ItpButton>
          <JobHandlerControl
            jobType="create_invoices_from_consumptions"
            load-current
            :payload="onCreateInvoicesPayload"
            icon="mdi:cog"
            :text="pgettext('MeterConsumptions|Submit', 'Generate Invoices')"
            :disabled="!selectedItems || !selectedItems.length"
            size="sm"
            class="ps-3 border-start"
          >
          </JobHandlerControl>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
